import React, { useMemo, useContext, useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiSettings, FiCalendar, FiTrendingUp, FiTarget, FiClock, FiList, FiAlertTriangle, FiCheckCircle, FiPackage } from 'react-icons/fi';
import * as sched from 'sched';
import { useTeam } from '../../hooks/useTeam';
import { useDatabase } from '../../hooks/useDatabase';
import { useSchedule } from '../../hooks/useSchedule';
import { usePlanningGrid } from '../../hooks/usePlanningGrid';
import { useTemplateCache } from '../../hooks/useTemplateCache';
import { WorkspaceContext } from '../../context/WorkspaceContext';
import { PlanningGrid } from '../planning/PlanningGrid';
import { AvailabilityHeatmap } from '../planning/AvailabilityHeatmap';
import { TeamForecast } from '../planning/TeamForecast';
import ResourceReservationPanel from '../planning/ResourceReservationPanel';
import PriorityItemsList from '../priorities/PriorityItemsList';
import ImprovementHelper from '../common/ImprovementHelper';

interface TeamDashboardProps {
  teamId: string;
}

/** Format a date string as "Mon Mar 12" */
function fmtDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

const TeamDashboard: React.FC<TeamDashboardProps> = ({ teamId }) => {
  const { team, loading: teamLoading, error: teamError } = useTeam(teamId);
  const { database } = useDatabase();
  const { safeData } = useContext(WorkspaceContext);
  const templateCache = useTemplateCache();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  // ─── Schedule (PlanningGrid) ──────────────────────────────────────
  const today = new Date().toISOString().slice(0, 10);

  // Read team's planning rhythm if set
  const teamRhythm = useMemo(() => {
    if (!database) return null;
    const wrapped = database.teams()[teamId];
    if (!wrapped) return null;
    const rhythm = wrapped.planning_rhythm();
    if (!rhythm || !rhythm.interval_days) return null;
    return rhythm as { interval_days: number; start_day: string };
  }, [database, teamId]);

  // Snap start date back to the beginning of the current rhythm period
  const periodStartDate = useMemo(() => {
    const DOW: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };
    const startDow = DOW[teamRhythm?.start_day ?? 'Mon'] ?? 1;
    const now = new Date();
    const nowDow = now.getDay(); // 0=Sun
    const daysBack = (nowDow - startDow + 7) % 7;
    const start = new Date(now);
    start.setDate(start.getDate() - daysBack);
    return start.toISOString().slice(0, 10);
  }, [teamRhythm]);

  // Schedule view: 6 rhythm periods from the period start
  const scheduleHorizon = useMemo(() => {
    const intervalDays = teamRhythm?.interval_days ?? 7;
    const d = new Date(periodStartDate);
    d.setDate(d.getDate() + intervalDays * 6);
    return d.toISOString().slice(0, 10);
  }, [periodStartDate, teamRhythm]);

  // Heatmap: 90 days for a wider view
  const heatmapHorizon = useMemo(() => {
    const d = new Date(periodStartDate);
    d.setDate(d.getDate() + 90);
    return d.toISOString().slice(0, 10);
  }, [periodStartDate]);

  const planningGrid = usePlanningGrid(teamId, periodStartDate, scheduleHorizon, teamRhythm);
  const heatmapGrid = usePlanningGrid(teamId, periodStartDate, heatmapHorizon, teamRhythm);

  // ─── Team-scoped deadlines & deliveries ───────────────────────────
  // We derive these from the fuzzy schedule: find outcomes connected to
  // this team's work by checking the schedule's node_to_entity mapping.
  const [teamMemberIds, setTeamMemberIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!database || !safeData) return;
    const t = database.teams()[teamId];
    if (!t) return;
    t.member_person_ids(safeData, true).then((ids: string[]) => setTeamMemberIds(new Set(ids)));
  }, [database, safeData, teamId]);

  const { schedule: fuzzySchedule } = useSchedule();

  // Collect outcomes with deadlines and projected completion dates from the fuzzy schedule
  const { deadlines, recentDeliveries, upcomingDeliveries } = useMemo(() => {
    if (!database || teamMemberIds.size === 0) {
      return { deadlines: [] as any[], recentDeliveries: [] as any[], upcomingDeliveries: [] as any[] };
    }

    const outcomes = database.outcomes();
    const tasks = database.tasks();
    const allDeadlines: { entityId: string; entityType: 'outcome' | 'task'; title: string; date: string; description?: string }[] = [];
    const recent: { outcomeId: string; title: string; completedDate?: string }[] = [];
    const upcoming: { outcomeId: string; title: string; projectedDate?: string; p10Date?: string; p90Date?: string }[] = [];

    // Build lookup: outcome ID → fuzzy schedule node ID (for projected dates)
    const outcomeNodeIds: Record<string, string> = {};
    const schedMeta = (fuzzySchedule as any)?.meta;
    const schedStats = (fuzzySchedule as any)?.stats;
    if (schedMeta?.node_to_entity) {
      for (const [nodeId, entity] of Object.entries(schedMeta.node_to_entity)) {
        if ((entity as any)?.type === 'Outcome') {
          outcomeNodeIds[(entity as any).id] = nodeId;
        }
      }
    }

    // Collect outcome deadlines
    for (const [id, outcome] of Object.entries(outcomes)) {
      const title = outcome.title();
      const dl = outcome.deadlines?.() ?? [];
      for (const d of dl) {
        allDeadlines.push({
          entityId: id,
          entityType: 'outcome',
          title,
          date: d.date,
          description: d.description ?? undefined,
        });
      }

      // Check if we have a projected completion date from the fuzzy schedule
      const nodeId = outcomeNodeIds[id];
      const stats = nodeId && schedStats ? (schedStats as any)[nodeId] : null;

      if (stats?.finish?.p50_date) {
        upcoming.push({
          outcomeId: id,
          title,
          projectedDate: stats.finish.p50_date,
          p10Date: stats.finish.p10_date,
          p90Date: stats.finish.p90_date,
        });
      }
    }

    // Collect task deadlines
    for (const [id, task] of Object.entries(tasks)) {
      const title = task.title();
      const dl = task.deadlines?.() ?? [];
      for (const d of dl) {
        allDeadlines.push({
          entityId: id,
          entityType: 'task',
          title,
          date: d.date,
          description: d.description ?? undefined,
        });
      }
    }

    allDeadlines.sort((a, b) => a.date.localeCompare(b.date));
    upcoming.sort((a, b) => (a.projectedDate ?? '').localeCompare(b.projectedDate ?? ''));

    return {
      deadlines: allDeadlines,
      recentDeliveries: recent,
      upcomingDeliveries: upcoming,
    };
  }, [database, teamMemberIds, fuzzySchedule]);

  // ─── Loading / error states ───────────────────────────────────────
  if (teamLoading) {
    return (
      <div className="flex items-center gap-3 justify-center p-12 text-cm-text-muted text-sm">
        <div className="w-5 h-5 border-2 border-cm-accent-orange border-t-transparent rounded-full animate-spin" />
        Loading team...
      </div>
    );
  }

  if (teamError || !team) {
    return <div className="p-4 text-red-400 text-sm">{teamError ?? 'Team not found'}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-mono font-semibold text-cm-text-primary">{team.name}</h2>
          <p className="text-sm text-cm-text-muted">
            {team.memberIds.length} member{team.memberIds.length !== 1 ? 's' : ''}
            {team.subTeamIds.length > 0 && ` · ${team.subTeamIds.length} sub-team${team.subTeamIds.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <Link
          to={`/workspaces/${workspaceId}/teams/${teamId}`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cm-border-subtle text-sm text-cm-text-muted hover:text-cm-text-primary hover:border-cm-border-mid transition-colors"
        >
          <FiSettings className="w-3.5 h-3.5" />
          Configure
        </Link>
      </div>

      {/* ── Availability Heatmap (90-day view) ─────────────────── */}
      {heatmapGrid.result && (
        <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-4">
          <div className="flex items-center gap-2 mb-3">
            <FiClock className="w-4 h-4 text-cm-accent-purple" />
            <h3 className="text-sm font-mono font-medium text-cm-text-primary">Availability</h3>
          </div>
          <AvailabilityHeatmap result={heatmapGrid.result} />
        </div>
      )}

      {/* ── Schedule (PlanningGrid) ─────────────────────────────── */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiCalendar className="w-5 h-5 text-cm-accent-blue" />
          <h3 className="font-mono font-medium text-cm-text-primary">Schedule</h3>
          <span className="text-xs text-cm-text-muted ml-auto">
            {teamRhythm ? `${teamRhythm.interval_days}d rhythm` : 'weekly (default)'}
          </span>
        </div>
        {planningGrid.loading ? (
          <div className="flex items-center gap-3 justify-center p-8 text-cm-text-muted text-sm">
            <div className="w-4 h-4 border-2 border-cm-accent-blue border-t-transparent rounded-full animate-spin" />
            Building schedule...
          </div>
        ) : planningGrid.error ? (
          <div className="p-4 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
            {planningGrid.error}
          </div>
        ) : planningGrid.result ? (
          <>
            <PlanningGrid
              result={planningGrid.result}
              onAcceptSuggestion={planningGrid.acceptSuggestion}
              onAddAllocation={planningGrid.addAllocationForCell}
              onRemoveDraft={planningGrid.removeDraftForCell}
            />
            {planningGrid.draftAllocations.length > 0 && (
              <div className="mt-3 flex items-center gap-3">
                <span className="text-xs text-cm-text-muted">
                  {planningGrid.draftAllocations.length} unsaved change{planningGrid.draftAllocations.length !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={planningGrid.commitDrafts}
                  className="px-3 py-1 rounded bg-cm-accent-green text-cm-bg-deep text-xs font-medium hover:opacity-90"
                >
                  Save
                </button>
                <button
                  onClick={planningGrid.discardDrafts}
                  className="px-3 py-1 rounded border border-cm-border-subtle text-xs text-cm-text-muted hover:text-cm-text-primary"
                >
                  Discard
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-cm-text-muted text-center py-6">
            No schedule data available. Assign team members and work matches to populate.
          </p>
        )}
      </div>

      {/* ── Resources ───────────────────────────────────────────── */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiPackage className="w-5 h-5 text-cm-accent-teal" />
          <h3 className="font-mono font-medium text-cm-text-primary">Resources</h3>
          <span className="text-xs text-cm-text-muted ml-auto">
            {teamRhythm ? `${teamRhythm.interval_days}d rhythm` : 'weekly (default)'}
          </span>
        </div>
        <ResourceReservationPanel
          teamId={teamId}
          startDate={periodStartDate}
          endDate={scheduleHorizon}
          intervalDays={teamRhythm?.interval_days ?? 7}
          workspaceId={workspaceId}
        />
      </div>

      {/* ── Upcoming Deadlines ──────────────────────────────────── */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiTarget className="w-5 h-5 text-cm-accent-orange" />
          <h3 className="font-mono font-medium text-cm-text-primary">Upcoming Deadlines</h3>
        </div>
        {deadlines.length === 0 ? (
          <p className="text-sm text-cm-text-muted text-center py-4">
            No deadlines set on tasks or outcomes connected to this team's work.
          </p>
        ) : (
          <div className="divide-y divide-cm-border-subtle">
            {deadlines.map((dl, i) => {
              const isPast = dl.date < today;
              const linkPath = dl.entityType === 'task'
                ? `/workspaces/${workspaceId}/tasks/${dl.entityId}`
                : `/workspaces/${workspaceId}/outcomes/${dl.entityId}`;
              return (
                <div key={`${dl.entityId}-${i}`} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Link
                      to={linkPath}
                      className="text-sm font-medium text-cm-text-primary hover:text-cm-accent-orange transition-colors truncate"
                    >
                      {dl.title}
                    </Link>
                    {dl.description && (
                      <span className="text-xs text-cm-text-muted truncate">{dl.description}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isPast ? (
                      <FiAlertTriangle className="w-3.5 h-3.5 text-red-400" title="Past deadline" />
                    ) : (
                      <FiCheckCircle className="w-3.5 h-3.5 text-cm-accent-green" title="On track" />
                    )}
                    <span className={`text-xs font-mono ${isPast ? 'text-red-400' : 'text-cm-text-secondary'}`}>
                      {fmtDate(dl.date)}
                    </span>
                    <ImprovementHelper
                      entityType="deadline"
                      entityName={dl.title}
                      target={{ kind: dl.entityType === 'task' ? 'Task' : 'Outcome', id: dl.entityId }}
                      ownerScope={{ kind: 'Team', id: teamId }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Deliveries (outcomes) ───────────────────────────────── */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiClock className="w-5 h-5 text-cm-accent-green" />
          <h3 className="font-mono font-medium text-cm-text-primary">Deliveries</h3>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h4 className="text-xs font-medium text-cm-text-muted uppercase tracking-wider mb-3">Recent</h4>
            {recentDeliveries.length === 0 ? (
              <p className="text-sm text-cm-text-muted text-center py-3 border border-dashed border-cm-border-mid rounded-lg">
                No completed outcomes yet
              </p>
            ) : (
              <div className="space-y-2">
                {recentDeliveries.map((d) => (
                  <Link
                    key={d.outcomeId}
                    to={`/workspaces/${workspaceId}/outcomes/${d.outcomeId}`}
                    className="block text-sm text-cm-text-primary hover:text-cm-accent-orange transition-colors"
                  >
                    {d.title}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div>
            <h4 className="text-xs font-medium text-cm-text-muted uppercase tracking-wider mb-3">Upcoming (projected)</h4>
            {upcomingDeliveries.length === 0 ? (
              <p className="text-sm text-cm-text-muted text-center py-3 border border-dashed border-cm-border-mid rounded-lg">
                No projected deliveries
              </p>
            ) : (
              <div className="divide-y divide-cm-border-subtle">
                {upcomingDeliveries.map((d) => (
                  <div key={d.outcomeId} className="py-2.5 flex items-center justify-between gap-3">
                    <Link
                      to={`/workspaces/${workspaceId}/outcomes/${d.outcomeId}`}
                      className="text-sm font-medium text-cm-text-primary hover:text-cm-accent-orange transition-colors truncate"
                    >
                      {d.title}
                    </Link>
                    {d.projectedDate && (
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs font-mono text-cm-text-secondary">
                          {fmtDate(d.projectedDate)}
                        </span>
                        {d.p10Date && d.p90Date && (
                          <span className="text-[10px] text-cm-text-muted" title={`P10: ${fmtDate(d.p10Date)} · P90: ${fmtDate(d.p90Date)}`}>
                            ({fmtDate(d.p10Date)} – {fmtDate(d.p90Date)})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Priorities ────────────────────────────────────────────── */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <FiList className="w-5 h-5 text-cm-accent-orange" />
            <h3 className="font-mono font-medium text-cm-text-primary">Priorities</h3>
          </div>
        </div>
        <p className="text-[11px] text-cm-text-muted mb-3">Priority order for this team's work.</p>
        {database && (
          <PriorityItemsList
            database={database as unknown as sched.WrappedDatabase}
            workspaceId={workspaceId}
            owner={{ type_: 'Team', id: teamId }}
            editable
          />
        )}
      </div>

      {/* ── Forecast (fuzzy Gantt) ──────────────────────────────── */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiTrendingUp className="w-5 h-5 text-cm-accent-purple" />
          <h3 className="font-mono font-medium text-cm-text-primary">Forecast</h3>
          <span className="text-xs text-cm-text-muted ml-auto">probabilistic schedule</span>
        </div>
        <TeamForecast teamId={teamId} />
      </div>
    </div>
  );
};

export default TeamDashboard;
